import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { PCMP_MIS_DEFAULT_TAB } from '../constants/pcmpMis.constants';
import type {
  PcmpMisBatchesParams,
  PcmpMisQueueParams,
  PcmpMisQueueSearch,
  PcmpMisView,
} from '../types/pcmpMis.types';
import { usePcmpQueueFilters } from './usePcmpQueueFilters';

const route = getRouteApi('/_auth/pcmp/mis/');

export function usePcmpMisFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const setSearch = useCallback(
    (update: (prev: PcmpMisQueueSearch) => PcmpMisQueueSearch) => {
      void navigate({ search: update });
    },
    [navigate],
  );

  const queue = usePcmpQueueFilters(search, setSearch);
  const { appliedFilters, page, limit } = queue;

  const tab = search.tab ?? PCMP_MIS_DEFAULT_TAB;

  /** The proposal list's params; null on the Batches tab. */
  const queueParams = useMemo<PcmpMisQueueParams | null>(
    () => (tab === 'BATCHES' ? null : { ...appliedFilters, tab, page, limit }),
    [appliedFilters, tab, page, limit],
  );

  const batchesParams = useMemo<PcmpMisBatchesParams>(
    () => ({ ...appliedFilters, page, limit }),
    [appliedFilters, page, limit],
  );

  const setTab = useCallback(
    (next: PcmpMisView) => {
      setSearch((prev) => ({
        ...prev,
        tab: next === PCMP_MIS_DEFAULT_TAB ? undefined : next,
        page: undefined,
      }));
    },
    [setSearch],
  );

  return { ...queue, tab, queueParams, batchesParams, setTab };
}

export type PcmpMisFilterController = ReturnType<typeof usePcmpMisFilters>;
